/**
 * NotificationItem — una fila del NotificationsPanel.
 *
 * Doc 21 Fase Notif-1.e. Renderiza:
 * - Icono según event_type (MessageSquare, GitBranch, Check, etc.).
 * - Mensaje localizado con interpolación del actor + objeto.
 * - Timestamp relativo ("hace 5 min" / "5 min ago").
 * - Punto azul si está unread.
 *
 * Click se delega al padre (panel) que marca como leído + navega.
 */

import {
  AlertTriangle,
  Check,
  GitBranch,
  MessageSquare,
  ShieldX,
  UserPlus,
  UserMinus,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { NotificationItem as NotificationItemType } from '../../api/notifications'

interface NotificationItemProps {
  item: NotificationItemType
  onClick: () => void
}

export function NotificationItem({ item, onClick }: NotificationItemProps) {
  const { t, i18n } = useTranslation('shell')
  const unread = item.read_at === null
  const { icon: Icon, iconClass } = iconForEvent(item.event_type)

  // Subject del template — interpola payload + actor name resuelto en runtime
  // (el payload trae artifact_name, project_name, etc. denormalizados).
  const message = renderEventMessage(t, item)
  const timeAgo = formatRelativeTime(item.created_at, i18n.language)

  return (
    <button
      type="button"
      onClick={onClick}
      className={
        unread
          ? 'w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-surface-muted bg-brand-soft/30 transition-colors'
          : 'w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-surface-muted transition-colors'
      }
    >
      {/* Icono */}
      <div
        className={`shrink-0 w-8 h-8 rounded-md ${iconClass} grid place-items-center mt-0.5`}
      >
        <Icon className="w-4 h-4" />
      </div>

      {/* Contenido */}
      <div className="flex-1 min-w-0">
        <p
          className={
            unread
              ? 'text-sm text-ink font-medium leading-snug'
              : 'text-sm text-ink-muted leading-snug'
          }
        >
          {message}
        </p>
        <p className="text-[11px] text-ink-subtle mt-1 font-mono">{timeAgo}</p>
      </div>

      {/* Indicador unread */}
      {unread && (
        <span
          className="shrink-0 w-2 h-2 rounded-full bg-brand mt-2.5"
          aria-label="unread"
        />
      )}
    </button>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────

function iconForEvent(eventType: string): {
  icon: LucideIcon
  iconClass: string
} {
  // Color/tono según naturaleza del evento (action vs info vs alert).
  if (eventType.startsWith('comment.')) {
    return {
      icon: MessageSquare,
      iconClass: 'bg-brand-soft text-brand',
    }
  }
  if (eventType === 'stage.activated') {
    return {
      icon: GitBranch,
      iconClass: 'bg-review-soft text-review',
    }
  }
  if (eventType === 'artifact.rejected') {
    return {
      icon: ShieldX,
      iconClass: 'bg-critical-soft text-critical',
    }
  }
  if (eventType === 'artifact.canonized') {
    return {
      icon: Check,
      iconClass: 'bg-official-soft text-official',
    }
  }
  if (eventType === 'member.added_to_project') {
    return {
      icon: UserPlus,
      iconClass: 'bg-brand-soft text-brand',
    }
  }
  if (eventType === 'member.removed_from_project') {
    return {
      icon: UserMinus,
      iconClass: 'bg-surface-muted text-ink-muted',
    }
  }
  if (eventType === 'member.raci_changed') {
    return {
      icon: Users,
      iconClass: 'bg-brand-soft text-brand',
    }
  }
  // ADR-0002 Fase 10.f.3 — drift detected en cross-refs.
  if (eventType === 'cross_ref.drift_detected') {
    return {
      icon: AlertTriangle,
      iconClass: 'bg-critical-soft text-critical',
    }
  }
  // Fallback
  return {
    icon: MessageSquare,
    iconClass: 'bg-surface-muted text-ink-muted',
  }
}

type TFn = ReturnType<typeof useTranslation>['t']

function renderEventMessage(t: TFn, item: NotificationItemType): string {
  const payload = item.payload || {}
  const actor = String(payload.actor_display_name ?? '')
  const artifactName = String(payload.artifact_name ?? '')
  const projectName = String(payload.project_name ?? '')

  // Cada event_type tiene una key i18n con interpolación.
  // Defaults razonables si la key no existe en el catálogo.
  const key = `notifications.event.${item.event_type.replace('.', '_')}`
  return t(key, {
    actor: actor || t('notifications.event_fallback_actor'),
    artifact: artifactName || t('notifications.event_fallback_artifact'),
    project: projectName || '',
    defaultValue: defaultMessageFor(item),
  })
}

function defaultMessageFor(item: NotificationItemType): string {
  // Fallback en español si i18n no tiene la key. Mejor algo que nada.
  const payload = item.payload || {}
  const actor = String(payload.actor_display_name ?? '')
  const artifactName = String(payload.artifact_name ?? '')
  if (item.event_type === 'comment.created') {
    return `${actor || 'Alguien'} comentó en ${artifactName}`
  }
  if (item.event_type === 'comment.replied') {
    return `${actor || 'Alguien'} respondió en ${artifactName}`
  }
  if (item.event_type === 'comment.status_changed') {
    return `Cambio de estado en un comentario de ${artifactName}`
  }
  if (item.event_type === 'stage.activated') {
    return `Tu aprobación es requerida: ${artifactName}`
  }
  if (item.event_type === 'artifact.rejected') {
    return `${artifactName} fue rechazado`
  }
  if (item.event_type === 'artifact.canonized') {
    return `${artifactName} fue aprobado`
  }
  if (item.event_type === 'member.added_to_project') {
    return `Te añadieron a un proyecto`
  }
  if (item.event_type === 'cross_ref.drift_detected') {
    const sourceArtifactName = String(payload.source_artifact_name ?? '')
    const consumerArtifactName = String(payload.consumer_artifact_name ?? '')
    return `Cambio en ${sourceArtifactName || 'un documento'} — revisar ${consumerArtifactName || 'un documento'}`
  }
  return item.event_type
}

function formatRelativeTime(isoString: string, locale: string): string {
  const date = new Date(isoString)
  const now = new Date()
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000)

  const lang = locale.startsWith('en') ? 'en' : 'es'
  const fmt = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' })

  if (diffSec < 60) return fmt.format(-diffSec, 'second')
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return fmt.format(-diffMin, 'minute')
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return fmt.format(-diffHr, 'hour')
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 7) return fmt.format(-diffDay, 'day')
  const diffWeek = Math.floor(diffDay / 7)
  if (diffWeek < 5) return fmt.format(-diffWeek, 'week')
  const diffMonth = Math.floor(diffDay / 30)
  if (diffMonth < 12) return fmt.format(-diffMonth, 'month')
  const diffYear = Math.floor(diffDay / 365)
  return fmt.format(-diffYear, 'year')
}
